import React, { useEffect, useState } from "react";
import {
    Grid,
    Card,
    CardContent,
    Typography,
    Box,
} from "@mui/material";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid
} from "recharts";
import {
    Dashboard as DashboardIcon,
    Pending as PendingIcon,
    DoneAll as DoneAllIcon,
    CheckCircle as CheckCircleIcon,
    Schedule as ScheduleIcon,
    History as HistoryIcon,
    HighlightOff as HighlightOffIcon,
    Help as HelpIcon,
    PauseCircleFilled as PauseIcon,
    Paid as PaidIcon,
} from "@mui/icons-material";
import { BASE_URL } from "../api/api";
import { useNavigate } from "react-router-dom";

const iconMap = {
    "Review Batches": { icon: <DashboardIcon />, color: "#1E88E5", path: "/invoice" },
    "Pending Approval": { icon: <PendingIcon />, color: "#FB8C00", path: "/approval" },
    "Partial Approval": { icon: <DoneAllIcon />, color: "#AB47BC", path: "/partialApproval" },
    "Approved": { icon: <CheckCircleIcon />, color: "#43A047", path: "https://bandlecrm.acrossassist.in" },
    "Scheduled": { icon: <ScheduleIcon />, color: "#1976D2", path: "https://bandlecrm.acrossassist.in" },
    "Paid Batches": { icon: <PaidIcon />, color: "#00C853", path: "/completed-batches" },
    "Payment History": { icon: <HistoryIcon />, color: "#757575", path: "https://bandlecrm.acrossassist.in" },
    "Rejected Cases": { icon: <HighlightOffIcon />, color: "#E53935", path: "/reajected-cases" },
    "Query Cases": { icon: <HelpIcon />, color: "#1E88E5", path: "/query-case" },
    "Hold Cases": { icon: <PauseIcon />, color: "#BDBDBD", path: "/hold-case" },
};

const Dashboard = () => {
    const [summaryData, setSummaryData] = useState([]);
    const navigate = useNavigate();

    useEffect(() => {
        fetch(`${BASE_URL}/GetBatchSummaryCounts`)
            .then((res) => res.json())
            .then((data) => {
                if (data.status && Array.isArray(data.summaryList)) {
                    setSummaryData(data.summaryList);
                }
            })
            .catch((err) => {
                console.error("Error fetching summary:", err);
            });
    }, []);

    const handleCardClick = (path) => {
        if (path.startsWith("http")) {
            window.open(path, "_blank");
        } else {
            navigate(path); 
        }
    };

    return (
        <Box sx={{ flexGrow: 1, padding: 4, background: "#F3F6FD", minHeight: "100vh" }}>
            <Grid container spacing={3}>
                {summaryData.map((item, idx) => {
                    const { icon, color, path } = iconMap[item.category] || {};
                    return (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={idx}>
                            <Card
                                onClick={() => path && handleCardClick(path)}
                                sx={{
                                    height: "100%",
                                    display: "flex",
                                    width: "200px",
                                    alignItems: "center",
                                    padding: 2,
                                    boxShadow: 3,
                                    borderLeft: `6px solid ${color || "#90A4AE"}`,
                                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                                    cursor: path ? "pointer" : "default",
                                    "&:hover": path
                                        ? { transform: "scale(1.03)", boxShadow: 6 }
                                        : {},
                                }}
                            >
                                <Box
                                    sx={{
                                        mr: 2,
                                        color: color || "primary.main",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    {icon && React.cloneElement(icon, { fontSize: "large" })}
                                </Box>
                                <CardContent sx={{ padding: 0 }}>
                                    <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                                        {item.totalCount}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {item.category}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>

            <Box mt={5}>
                <Card sx={{ padding: 3, boxShadow: 4 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Batch Summary Overview
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={summaryData} margin={{ top: 20, right: 30, left: 10, bottom: 50 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="category" angle={-30} textAnchor="end" interval={0} />
                            <YAxis width={40} allowDecimals={false} />
                            <Tooltip />
                            <Bar dataKey="totalCount" fill="#1976D2" barSize={40} />
                        </BarChart>
                    </ResponsiveContainer>
                </Card>
            </Box>
        </Box>
    );
};

export default Dashboard;
